/**
 * Sistema de logging centralizado para BeautyBook
 *
 * Reemplaza los 188+ console.log dispersos con una interfaz consistente.
 *
 * Características:
 * - Niveles de log: debug, info, warn, error
 * - Deshabilitado automáticamente en producción
 * - Contexto opcional para agrupar logs relacionados
 * - Formato consistente con emojis
 * - Preparado para integración con servicios de monitoreo (Sentry, etc.)
 *
 * @example
 * import { logger } from '@/lib/logger';
 *
 * logger.info('Usuario autenticado', { userId: user.id });
 * logger.error('Error al cargar datos', error);
 * logger.debug('Estado actual', { bookings });
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

interface LoggerConfig {
  /**
   * Habilitar logs incluso en producción
   * @default false
   */
  enableInProduction?: boolean;

  /**
   * Nivel mínimo de log a mostrar
   * @default 'debug'
   */
  minLevel?: LogLevel;

  /**
   * Función personalizada para enviar errores a servicios externos
   * (ej: Sentry, LogRocket, etc.)
   */
  errorReporter?: (error: Error, context?: LogContext) => void;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LOG_EMOJIS: Record<LogLevel, string> = {
  debug: '🔍',
  info: '✅',
  warn: '⚠️',
  error: '❌',
};

class Logger {
  private config: LoggerConfig;
  private isDevelopment: boolean;

  constructor(config: LoggerConfig = {}) {
    this.config = {
      enableInProduction: false,
      minLevel: 'debug',
      ...config,
    };
    this.isDevelopment = import.meta.env.DEV;
  }

  /**
   * Verifica si el log debe mostrarse según el nivel y el entorno
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.isDevelopment && !this.config.enableInProduction) {
      return false;
    }

    const currentLevelValue = LOG_LEVELS[level];
    const minLevelValue = LOG_LEVELS[this.config.minLevel || 'debug'];

    return currentLevelValue >= minLevelValue;
  }

  /**
   * Formatea el mensaje con emoji y timestamp
   */
  private format(level: LogLevel, message: string): string {
    const emoji = LOG_EMOJIS[level];
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0]; // HH:MM:SS
    return `${emoji} [${timestamp}] ${message}`;
  }

  /**
   * Log interno que maneja el formato y contexto
   */
  private log(
    level: LogLevel,
    message: string,
    contextOrError?: LogContext | Error
  ): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const formattedMessage = this.format(level, message);

    // Determinar el método de console a usar
    const consoleMethod = level === 'debug' ? 'log' : level;

    if (contextOrError instanceof Error) {
      console[consoleMethod](formattedMessage, contextOrError);

      // Enviar error a servicio externo si está configurado
      if (level === 'error' && this.config.errorReporter) {
        this.config.errorReporter(contextOrError);
      }
    } else if (contextOrError && Object.keys(contextOrError).length > 0) {
      console[consoleMethod](formattedMessage, contextOrError);
    } else {
      console[consoleMethod](formattedMessage);
    }
  }

  /**
   * Log de debugging - información técnica detallada
   *
   * @example
   * logger.debug('Estado de la consulta', { data, loading, error });
   */
  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  /**
   * Log informativo - flujo normal de la aplicación
   *
   * @example
   * logger.info('Reservas cargadas', { count: bookings.length });
   */
  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  /**
   * Log de advertencia - situación anormal pero no crítica
   *
   * @example
   * logger.warn('Reserva sin servicio', { bookingId });
   */
  warn(message: string, context?: LogContext | Error): void {
    this.log('warn', message, context);
  }

  /**
   * Log de error - situación crítica que requiere atención
   *
   * @example
   * logger.error('Error al cargar reservas', error);
   */
  error(message: string, error?: Error | LogContext): void {
    this.log('error', message, error);
  }

  /**
   * Crea un logger con contexto prefijado
   *
   * Útil para agrupar logs de un módulo/componente específico
   *
   * @example
   * const bookingLogger = logger.withContext('Bookings');
   * bookingLogger.info('Cargando datos'); // ✅ [12:34:56] [Bookings] Cargando datos
   */
  withContext(prefix: string): Logger {
    const contextLogger = new Logger(this.config);

    // Override los métodos para agregar el prefijo
    const originalLog = contextLogger.log.bind(contextLogger);
    contextLogger.log = (level: LogLevel, message: string, context?: LogContext | Error) => {
      originalLog(level, `[${prefix}] ${message}`, context);
    };

    return contextLogger;
  }

  /**
   * Agrupa logs relacionados visualmente en la consola
   *
   * @example
   * logger.group('Procesando reserva #123', () => {
   *   logger.info('Validando datos');
   *   logger.info('Guardando en DB');
   *   logger.info('Enviando email');
   * });
   */
  group(label: string, callback: () => void): void {
    if (!this.shouldLog('debug')) {
      callback();
      return;
    }

    console.group(this.format('info', label));
    try {
      callback();
    } finally {
      console.groupEnd();
    }
  }
}

/**
 * Instancia por defecto del logger
 *
 * Configurada para el comportamiento estándar de la aplicación
 */
export const logger = new Logger();

/**
 * Exportar la clase para crear loggers personalizados si es necesario
 */
export { Logger };

/**
 * Exportar tipos para uso en la aplicación
 */
export type { LogLevel, LogContext, LoggerConfig };
